import { useCallback, useState, useRef, useLayoutEffect, useMemo } from 'react';
import { Flex } from 'rebass/styled-components';
import styled from 'styled-components/macro';
import { func } from 'prop-types';
import { trim, isEmpty } from 'lodash';
import { useRecoilValue } from 'recoil';
import clsx from 'clsx';
import debounce from 'lodash/debounce';

import { rem } from '@blueprint/ui';

import { TextInput, Button, Typography } from 'components/blueprint';
import { breakpoints } from 'themes';
import { useSafeLabel } from 'hooks';
import { handleEnterEvent, KeyCode } from 'utils/keyEventHandlerCreator';
import HorizontalRule from 'components/Ruler/HorizontalRule';
import { hasAgentConversationStartedState } from 'state/hasAgentConversationStarted';
import { sendMessage } from 'genesys/genesys';
import { useInteractionTracking } from 'tracking/hooks/useInteractionTracking';
import { getCircularNextIndex } from 'utils/getCircularNextIndex';
import { useOnWindowResize } from 'hooks/useOnWindowResize';
import { ScreenReaderOnly } from 'components/ScreenReaderOnly/ScreenReaderOnly';
import { useIsGenesysReAuthenticating } from 'hooks/useIsGenesysReAuthenticating';
import { useLastSentToAgentMessage } from 'hooks/useLastSentToAgentMessage';

import { useIsWaitingForFeedback } from './useIsWaitingForFeedback';
import { SuggestionList } from './SuggestionList';
import { useTypeAheadQuery } from '../useTypeAheadQuery';
import { getSuggestionListPositionStyle } from './getSuggestionListPositionStyle';
import { getListItemId, SuggestionListItem } from './SuggestionListItem';
import messages from './messages';

const SUGGESTION_LIST_ID = 'get-support-suggestion-list';
const ENTRY_INPUT_ID = 'get-support-query-input-box';

const EntryInputType = {
  FreeText: 'FreeText',
  TypeAheadFaq: 'TypeAheadFaq',
};

const INITIAL_ENTRY_INPUT = {
  text: '',
  type: EntryInputType.FreeText,
};

const EntryTextInput = styled(TextInput)`
  @media (max-width: ${breakpoints.md}px) {
    && {
      margin: ${rem(12)} auto;
      width: 100%;
    }
  }
`;

const SendQueryButton = styled(Button)`
  && {
    width: ${rem(96)};
  }

  @media (max-width: ${breakpoints.md}px) {
    && {
      margin: 0;
      width: 100%;
    }
  }
`;

const getActivedeScendant = (selectedIndex, maxIndex) =>
  selectedIndex >= 0 && selectedIndex <= maxIndex
    ? getListItemId(selectedIndex)
    : '';

const shouldSendTypeAheadQuery = (inputValue, hasAgentConversationStarted) =>
  inputValue?.length > 2 && !hasAgentConversationStarted;

/**
 * EntryInput
 * Free text input and send button
 */
export const EntryInput = ({ onSendQuery, onTypeAheadSelect, isError }) => {
  const [entryInput, setEntryInput] = useState(INITIAL_ENTRY_INPUT);
  const inputRef = useRef(null);
  const hasAgentConversationStarted = useRecoilValue(
    hasAgentConversationStartedState,
  );
  const {
    typeAheadSuggestionList,
    hasSuggestionsItems,
    sendTypeAheadBotQuery,
  } = useTypeAheadQuery();
  const [canSuggestionListOpen, setCanSuggestionListOpen] = useState(false);
  const isSuggestionListOpen = canSuggestionListOpen && hasSuggestionsItems;

  const [selectedSuggestItemIndex, setSelectedSuggestItemIndex] = useState(-1);
  const [suggestionListPosition, setSuggestionListPosition] = useState({});
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  const safeLabel = useSafeLabel();

  const isWaitingForFeedback = useIsWaitingForFeedback();

  const { isGenesysReAuthenticating } = useIsGenesysReAuthenticating();

  const { setLastSentToAgentMessage } = useLastSentToAgentMessage();

  const inputBoxPlaceholderText = safeLabel(
    isWaitingForFeedback
      ? messages.feedbackInputPlaceholder
      : messages.questionInputPlaceholder,
  );

  const trackEntry = useInteractionTracking('input');
  const trackClick = useInteractionTracking('button click');

  const dismissSuggestionList = () => {
    setCanSuggestionListOpen(false);
    setSelectedSuggestItemIndex(-1);
  };

  const debouncedSendTypeAheadBotQuery = useMemo(
    () => debounce(sendTypeAheadBotQuery, 500),
    [sendTypeAheadBotQuery],
  );

  const handleInputChange = useCallback(
    ({ value }) => {
      setEntryInput({
        text: value,
        type: EntryInputType.FreeText,
      });

      if (shouldSendTypeAheadQuery(value, hasAgentConversationStarted)) {
        debouncedSendTypeAheadBotQuery(value);
        setCanSuggestionListOpen(true);
      } else {
        dismissSuggestionList();
      }
    },
    [hasAgentConversationStarted, debouncedSendTypeAheadBotQuery],
  );

  const sendAgentMessage = (message) => {
    sendMessage({ message });
    setLastSentToAgentMessage(message);
  };

  const handleSendQuery = () => {
    if (entryInput.type === EntryInputType.TypeAheadFaq) {
      const { AnswerLinkId, RecognitionId, QuestionText } = entryInput.data;

      setEntryInput(INITIAL_ENTRY_INPUT);

      onTypeAheadSelect({
        answer_id: AnswerLinkId?.[0],
        recognition_id: RecognitionId?.[0],
        faqrequest: QuestionText?.[0],
        selectSource: 'autocomplete',
      });

      return;
    }

    const trimmed = trim(entryInput.text);

    if (!isEmpty(trimmed)) {
      !hasAgentConversationStarted
        ? onSendQuery(trimmed)
        : sendAgentMessage(trimmed);

      setEntryInput(INITIAL_ENTRY_INPUT);
    }
  };

  const handleComboboxKeyEvent = (event) => {
    if (isSuggestionListOpen) {
      if (event.keyCode === KeyCode.DOWN_ARROW) {
        setSelectedSuggestItemIndex((previousIndex) =>
          getCircularNextIndex(
            previousIndex,
            typeAheadSuggestionList.length,
            'forward',
          ),
        );
      }

      if (event.keyCode === KeyCode.UP_ARROW) {
        setSelectedSuggestItemIndex((previousIndex) =>
          getCircularNextIndex(
            previousIndex,
            typeAheadSuggestionList.length,
            'backward',
          ),
        );
      }

      if (event.keyCode === KeyCode.ESCAPE) {
        dismissSuggestionList();
      }

      if (event.keyCode === KeyCode.ENTER) {
        if (selectedSuggestItemIndex > -1) {
          onSelectSuggestionItem(
            typeAheadSuggestionList[selectedSuggestItemIndex],
          );
        }

        dismissSuggestionList();
      }
    } else {
      if (event.keyCode === KeyCode.DOWN_ARROW) {
        setCanSuggestionListOpen(true);
      }

      if (event.keyCode === KeyCode.ENTER) {
        handleSendQuery();
        dismissSuggestionList();
      }
    }
  };

  const onSelectSuggestionItem = (item) => {
    const { QuestionText } = item;

    setEntryInput({
      text: QuestionText?.[0],
      type: EntryInputType.TypeAheadFaq,
      data: item,
    });
    setCanSuggestionListOpen(false);
  };

  useOnWindowResize(() => {
    setViewportHeight(window.innerHeight);
  });

  /*
   * Set suggestion list position
   */
  useLayoutEffect(() => {
    if (isSuggestionListOpen && inputRef.current) {
      const suggestionListEle = document.querySelector(
        `#${SUGGESTION_LIST_ID}`,
      );

      setSuggestionListPosition(
        getSuggestionListPositionStyle(
          suggestionListEle,
          inputRef.current,
          viewportHeight,
        ),
      );
    }
  }, [isSuggestionListOpen, viewportHeight]);

  return (
    <Flex flexDirection="column">
      <HorizontalRule paddingLeft={rem(20)} marginTop={rem(20)} />

      <Flex
        sx={{
          position: 'relative',
          [`@media screen and (max-width: ${breakpoints.md}px)`]: {
            flexDirection: 'column',
          },
        }}
      >
        {hasAgentConversationStarted ? (
          <EntryTextInput
            ref={inputRef}
            marginTop={rem(20)}
            marginBottom="0"
            width={rem(346)}
            value={entryInput.text}
            onChange={trackEntry({
              onChange: handleInputChange,
              tealiumData: {
                event_content: 'freeform question entered',
              },
            })}
            aria-label={inputBoxPlaceholderText}
            placeholder={inputBoxPlaceholderText}
            onKeyDown={handleEnterEvent(handleSendQuery)}
            id={ENTRY_INPUT_ID}
            data-testid={ENTRY_INPUT_ID}
            flexGrow={1}
            maxLength={4070}
            disabled={isError || isGenesysReAuthenticating}
          />
        ) : (
          <>
            <EntryTextInput
              ref={inputRef}
              marginTop={rem(20)}
              marginBottom="0"
              width={rem(346)}
              value={entryInput.text}
              onChange={trackEntry({
                onChange: handleInputChange,
                tealiumData: {
                  event_content: 'freeform question entered',
                },
              })}
              aria-label={inputBoxPlaceholderText}
              placeholder={inputBoxPlaceholderText}
              onKeyUp={handleComboboxKeyEvent}
              aria-haspopup="listbox"
              aria-autocomplete="list"
              maxLength={4070}
              role="combobox"
              id={ENTRY_INPUT_ID}
              data-testid={ENTRY_INPUT_ID}
              flexGrow={1}
              aria-expanded={isSuggestionListOpen}
              aria-controls={SUGGESTION_LIST_ID}
              aria-activedescendant={getActivedeScendant(
                selectedSuggestItemIndex,
                typeAheadSuggestionList.length - 1,
              )}
              disabled={isError}
            />
            <ScreenReaderOnly role="status" aria-atomic="true">
              {typeAheadSuggestionList.length > 0
                ? safeLabel(messages.suggestionListAvailableResults, {
                    count: typeAheadSuggestionList.length,
                  })
                : safeLabel(messages.suggestionListNoAvailableResults)}
            </ScreenReaderOnly>
          </>
        )}
        <SendQueryButton
          variant="light.primary"
          marginLeft={rem(12)}
          marginTop={rem(20)}
          disabled={!entryInput.text}
          onClick={handleSendQuery}
        >
          {safeLabel(messages.sendQuery)}
        </SendQueryButton>
        <SuggestionList
          className={clsx({ show: isSuggestionListOpen })}
          id={SUGGESTION_LIST_ID}
          data-testid={SUGGESTION_LIST_ID}
          role="listbox"
          aria-label={safeLabel(messages.suggestionQueryList)}
          listStyleType="none"
          style={{
            ...suggestionListPosition,
          }}
        >
          {typeAheadSuggestionList.map((option, index) => (
            <SuggestionListItem
              id={getListItemId(index)}
              data-testid={getListItemId(index)}
              role="option"
              key={`${option.RecognitionId}-${index}`}
              onClick={trackClick({
                onClick: () => onSelectSuggestionItem(option),
                tealiumData: {
                  event_content: `suggested faq selected: ${option.QuestionText?.[0]}`,
                },
              })}
              aria-selected={index === selectedSuggestItemIndex || undefined}
              className={clsx({ selected: index === selectedSuggestItemIndex })}
            >
              <Typography lineHeight={rem(20)}>
                {safeLabel(option.QuestionText?.[0])}
              </Typography>
            </SuggestionListItem>
          ))}
        </SuggestionList>
      </Flex>
    </Flex>
  );
};

EntryInput.propTypes = {
  /*
   * Callback for send query
   */
  onSendQuery: func,
  onTypeAheadSelect: func,
};
